import { ScrollStory, StageCanvas, StageCard, StagePanels, type StoryBeat } from '../../../../components/ScrollStory';

/**
 * "How it works" for Blacklist Checks — the check runs as an enrichment pass inside a Domain Health
 * snapshot. Grounded in the code (BlacklistService.check with RBL_ZONES + DBL_ZONE, the 4s per-zone
 * timeout, maybeCheckBlacklist folding hits onto the domain_health row, the clean→listed edge that
 * fires domain.blacklisted). Honesty guardrails: the in-house checker is 13 DNSBLs (12 IP RBLs +
 * Spamhaus DBL), not "50+/90+"; there is no nightly cron — re-checks are lazy and TTL-capped at 24h;
 * a paid provider fallback runs only when every in-house list is inconclusive.
 */

const STAGES = ['Piggyback', 'The lists', 'Query', 'Aggregate', 'Edge alert', 'Surface'];

type Beat = StoryBeat & { detail: React.ReactNode };

const BEATS: Beat[] = [
  {
    n: 1,
    kicker: 'Piggyback',
    title: 'Rides on Domain Health',
    body: (
      <>
        A blacklist check never runs alone — it&rsquo;s a best-effort pass inside every fresh Domain Health snapshot. So
        the hit count lands right next to SPF / DKIM / DMARC posture, sharing the same 24-hour cache. No separate
        endpoint, no second console screen.
      </>
    ),
    facts: ['inside getOrFetch', 'shares 24h cache', 'best-effort'],
    detail: (
      <StageCard head="Enrichment pass">
        <div className="font-mono text-[11px] md:text-[12.5px]">
          <div className="text-faint">Domain Health snapshot · acme.co</div>
          <div className="text-review">+ blacklist check</div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          a failure never blocks the DNS snapshot
        </div>
      </StageCard>
    ),
  },
  {
    n: 2,
    kicker: 'The lists',
    title: '13 lists, IPs and domains',
    body: (
      <>
        We query a curated set of 13 DNSBLs — 12 IP-based lists (Spamhaus ZEN, Barracuda, SpamCop, SORBS, CBL,
        UCEPROTECT and more) plus Spamhaus&rsquo;s domain block list. A domain resolves to its A record for the IP
        lists; the DBL is queried on the domain itself.
      </>
    ),
    facts: ['12 IP RBLs', '+ Spamhaus DBL', 'IPs and domains'],
    detail: (
      <StageCard head="Blacklist zones">
        <div className="flex flex-wrap gap-1.5 font-mono text-[10px] md:text-[11px]">
          {['Spamhaus ZEN', 'Barracuda', 'SpamCop', 'SORBS', 'CBL', 'UCEPROTECT', 'Mailspike', 'DBL'].map((z) => (
            <span key={z} className="rounded border border-border px-1.5 py-0.5 text-muted">
              {z}
            </span>
          ))}
        </div>
        <div className="mt-2 font-mono text-[10.5px] text-faint md:text-[11px]">network-tier lists flagged informational</div>
      </StageCard>
    ),
  },
  {
    n: 3,
    kicker: 'Query',
    title: 'All zones at once',
    body: (
      <>
        The IP is reversed and queried against every zone in parallel — each racing a{' '}
        <span className="font-mono">4-second</span> timeout, so total wall time stays under ~5s even if half the lists
        stall. A refusal code from a public resolver reads as &ldquo;couldn&rsquo;t check,&rdquo; never a false listing.
      </>
    ),
    facts: ['reversed-IP lookup', 'parallel · 4s each', 'refusal ≠ listed'],
    detail: (
      <StageCard head="Parallel probes">
        <div className="grid grid-cols-1 gap-y-1 font-mono text-[11px] md:text-[12.5px]">
          <ZoneRow zone="zen.spamhaus.org" status="clean" />
          <ZoneRow zone="b.barracudacentral" status="clean" />
          <ZoneRow zone="bl.spamcop.net" status="listed" hot />
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">host resolver · not a public open one</div>
      </StageCard>
    ),
  },
  {
    n: 4,
    kicker: 'Aggregate',
    title: 'Count the hits, stamp the row',
    body: (
      <>
        Reputation-tier listings are counted (network/ASN lists excluded), capped at 20, and each keeps its name + URL.
        The count stamps <span className="font-mono">blacklistHits</span> straight onto the Domain Health row. If every
        list is inconclusive, a paid provider fallback fills in.
      </>
    ),
    facts: ['reputation-tier only', 'capped at 20', 'folded onto DH row'],
    detail: (
      <StageCard head="Aggregate">
        <div className="font-mono text-[11.5px] md:text-[12.5px]">
          <div className="flex items-center justify-between">
            <span className="text-muted">listed (reputation-tier)</span>
            <span className="font-semibold text-review">1</span>
          </div>
          <div className="mt-1 flex items-center justify-between border-t border-bordersoft pt-1">
            <span className="font-semibold">1 hit / 13 checked</span>
            <span className="rounded-full bg-review/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-review">
              flagged
            </span>
          </div>
        </div>
      </StageCard>
    ),
  },
  {
    n: 5,
    kicker: 'Edge alert',
    title: 'Only on the clean→listed edge',
    body: (
      <>
        When a domain with zero prior hits first shows <span className="font-mono">≥1</span>,{' '}
        <span className="font-mono">domain.blacklisted</span> fires — once, on the transition, idempotent across repeat
        re-checks. Your on-call hears the moment a clean domain gets listed, before it hurts sending reputation.
      </>
    ),
    facts: ['clean → listed edge', 'fires once', 'first-ever check is silent'],
    detail: (
      <StageCard head="Webhook — domain.blacklisted">
        <pre className="overflow-x-auto font-mono text-[10.5px] leading-relaxed text-txt md:text-[12px]">
          <code>{`{ "event": "domain.blacklisted",
  "domain": "acme.co", "blacklistHits": 1,
  "blacklistListings": [{ "name": "SpamCop",
    "url": "https://spamcop.net/…" }] }`}</code>
        </pre>
      </StageCard>
    ),
  },
  {
    n: 6,
    kicker: 'Surface',
    title: 'Where the listing shows up',
    body: (
      <>
        A <span className="font-mono">&ldquo;N hits / M checked&rdquo;</span> tile sits on the domain page and as a
        sub-pill on the record badge. Drill in for each listing&rsquo;s name and delisting URL; the Analytics tab ranks
        which lists are most aggressive across your fleet.
      </>
    ),
    facts: ['per-domain tile', 'per-listing URL', 'fleet roll-up'],
    detail: (
      <StageCard head="On the record">
        <div className="flex items-center gap-2 text-[12px] md:text-[13px]">
          <span className="font-mono text-muted">acme.co</span>
          <span className="rounded-full bg-accent/15 px-2 py-0.5 font-mono text-[10px] font-bold text-accent">
            95
          </span>
          <span className="rounded-full bg-review/15 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.06em] text-review">
            1 hit / 13
          </span>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          click a listing → its own delisting form
        </div>
      </StageCard>
    ),
  },
];

export function BlacklistStory() {
  const panels = BEATS.map((b) => ({ n: b.n, content: b.detail }));
  return (
    <section className="mt-20" aria-label="How blacklist checks work">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-review">
          <span className="h-1.5 w-1.5 rounded-full bg-review" /> How it works
        </span>
        <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
          13 DNSBLs on every <span className="font-mono text-review">Domain Health</span> snapshot
        </h2>
        <p className="mt-3 text-[14.5px] leading-relaxed text-muted">
          Follow <span className="font-mono">acme.co</span> through the blacklist pass — a parallel sweep of every zone,
          hits folded onto the health row, and a webhook the instant a clean domain gets listed.
        </p>
      </div>

      <ScrollStory
        beats={BEATS}
        className="mt-6"
        renderStage={(beat, p) => (
          <StageCanvas>
            <div className="absolute inset-x-4 top-4 md:inset-x-6 md:top-6">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-faint md:text-[11px]">
                <span>Blacklist pipeline</span>
                <span>step {beat}/6</span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-border">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-review to-verify transition-all duration-500"
                  style={{ width: `${(beat / 6) * 100}%` }}
                />
              </div>
              <div className="mt-2 font-mono text-[12px] text-review md:text-[13px]">{STAGES[beat - 1]}</div>
              <span className="sr-only">{`Progress ${Math.round(p * 100)}%`}</span>
            </div>
            <StagePanels beat={beat} panels={panels} />
          </StageCanvas>
        )}
      />
    </section>
  );
}

function ZoneRow({ zone, status, hot }: { zone: string; status: string; hot?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-faint">{zone}</span>
      <span className={hot ? 'font-semibold text-review' : 'text-allow'}>{status}</span>
    </div>
  );
}
